/**
 * ImageGridItem - Memoised image grid item component.
 *
 * Extracted from RefinementFlow to prevent cascade re-renders when hover
 * state changes on other items. Only re-renders when its own props change.
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners
 * - Mid-green selection states
 * - WCAG 2.1 AA compliant
 */

import { memo, useCallback } from 'react'
import type { ImageVariation } from '@/services/api'

export interface ImageGridItemProps {
  /**
   * The image variation to display
   */
  variation: ImageVariation
  /**
   * Index of this item in the grid (0-3)
   */
  index: number
  /**
   * Whether this item is currently selected
   */
  isSelected: boolean
  /**
   * Whether this item is currently hovered
   */
  isHovered: boolean
  /**
   * Whether interaction is disabled (e.g., during loading)
   */
  isDisabled: boolean
  /**
   * Callback when user selects this item
   */
  onSelect: (index: number) => void
  /**
   * Callback when mouse enters this item
   */
  onMouseEnter: (index: number) => void
  /**
   * Callback when mouse leaves this item
   */
  onMouseLeave: () => void
  /**
   * Callback when user requests fullscreen preview
   */
  onPreview: (variation: ImageVariation) => void
}

/**
 * Memoised image grid item - only re-renders when its own props change.
 * Prevents cascade re-renders when hover state changes on other items.
 */
export const ImageGridItem = memo(function ImageGridItem({
  variation,
  index,
  isSelected,
  isHovered,
  isDisabled,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  onPreview,
}: ImageGridItemProps) {
  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onSelect(index)
    }
  }, [isDisabled, onSelect, index])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (!isDisabled) {
          onSelect(index)
        }
      }
    },
    [isDisabled, onSelect, index]
  )

  const handleMouseEnter = useCallback(() => {
    onMouseEnter(index)
  }, [onMouseEnter, index])

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onPreview(variation)
    },
    [onPreview, variation]
  )

  return (
    <div
      className={`
        group relative aspect-square
        transition-transform duration-200
        ${isSelected ? 'scale-[0.98]' : ''}
        ${isHovered && !isSelected ? '-translate-y-1' : ''}
        animate-fade-in
      `}
      style={{ animationDelay: `${index * 150}ms` }}
    >
      <div
        role="radio"
        aria-checked={isSelected}
        aria-label={`Vision option ${index + 1}`}
        tabIndex={isDisabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`
          absolute inset-0 overflow-hidden
          border border-dark-green/15 rounded-2xl
          transition-shadow duration-200
          focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green
          ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        `}
        style={{
          boxShadow: isSelected
            ? 'inset 0 0 0 4px var(--color-mid-green, #40aa3b), var(--shadow-denizen)'
            : isHovered
              ? 'var(--shadow-denizen)'
              : 'var(--shadow-denizen-sm)',
        }}
      >
        {/* Image */}
        <img
          src={variation.image_url}
          alt={`Generated vision option ${index + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Selection overlay */}
        {isSelected && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-mid-green/20"
          >
            <div
              className="w-16 h-16 border border-dark-green/15 rounded-full flex items-center justify-center bg-mid-green shadow-denizen-sm"
            >
              <svg
                className="w-8 h-8 text-dark-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Number badge */}
        <div
          className="absolute top-3 left-3 w-10 h-10 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-lg shadow-denizen-sm"
          style={{
            backgroundColor: isSelected ? 'var(--color-mid-green, #40aa3b)' : '#FFFFFF',
          }}
        >
          {index + 1}
        </div>
      </div>

      {/* Fullscreen preview — sibling of radio so the two interactives are not nested.
          Real <button> for keyboard access (WCAG 2.1.1). */}
      <button
        type="button"
        onClick={handlePreviewClick}
        disabled={isDisabled}
        aria-label={`View image ${index + 1} fullscreen`}
        className={`
          absolute top-3 right-3 w-10 h-10 border border-dark-green/15 rounded-full
          flex items-center justify-center bg-white z-10
          transition-all duration-200
          hover:scale-110 hover:-translate-y-0.5
          opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100
          shadow-denizen-sm
          focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green
          disabled:cursor-not-allowed disabled:opacity-50
          ${isHovered || isSelected ? 'md:opacity-100' : ''}
        `}
      >
        <svg
          className="w-5 h-5 text-dark-green"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
          />
        </svg>
      </button>
    </div>
  )
})

export default ImageGridItem
